import { z } from 'zod';
import { toDocumentId, toModelId, toRevisionId } from '@/shared/types/common.types';
import { tolerantEnum, v1DateSchema } from '@/shared/utils/api/serverPrimitives';
import { REVISION_STATUSES } from '@/features/documents/types/document.types';
import { CHECK_STATES, HEALTH_STATUSES } from '../types/rollup.types';
import type { ProjectHealth, ProjectSummary } from '../types/rollup.types';

/**
 * The wire contract for `GET /api/v1/projects/{id}/summary` and `.../health`, from Appendix A
 * ("Project summary and roll-ups") of the plan in Conbent's
 * `docs/features/project-documents-implementation.md`.
 */
const count = z.number().int().nonnegative();

export const projectSummarySchema = z.object({
  models: z.object({
    total: count,
    byDiscipline: z.array(
      z.object({
        discipline: z.string().nullable(),
        count,
        lastSynchronized: v1DateSchema.nullable(),
      })
    ),
  }),
  documents: z.object({
    total: count,
    byStatus: z.array(z.object({ status: tolerantEnum(REVISION_STATUSES), count })),
    recent: z.array(
      z.object({
        id: z.string().uuid(),
        title: z.string(),
        documentNumber: z.string().nullable(),
        status: tolerantEnum(REVISION_STATUSES),
        updatedAt: v1DateSchema,
        updatedBy: z.object({ subject: z.string(), displayName: z.string() }),
      })
    ),
  }),
  sheets: z.object({ total: count }),
  members: z.object({ total: count, pending: count }),
  activeManifest: z
    .object({
      documentId: z.string().uuid(),
      documentNumber: z.string().nullable(),
      revisionId: z.string().uuid(),
      revisionCode: z.string(),
    })
    .nullable(),
});

export const projectHealthSchema = z.object({
  generatedAt: v1DateSchema,
  degraded: z.boolean(),
  overall: z.object({
    status: tolerantEnum(HEALTH_STATUSES),
    models: count,
    ready: count,
    attention: count,
    noData: count,
  }),
  models: z.array(
    z.object({
      modelId: z.number().int().positive(),
      title: z.string(),
      discipline: z.string().nullable(),
      status: tolerantEnum(HEALTH_STATUSES),
      modelVersion: z.number().int().positive().nullable(),
      lastSynchronized: v1DateSchema.nullable(),
      checks: z.array(
        z.object({
          key: z.string(),
          label: z.string(),
          state: tolerantEnum(CHECK_STATES),
          detail: z.string(),
        })
      ),
    })
  ),
});

export function toProjectSummary(raw: z.output<typeof projectSummarySchema>): ProjectSummary {
  return {
    ...raw,
    documents: {
      ...raw.documents,
      recent: raw.documents.recent.map((document) => ({ ...document, id: toDocumentId(document.id) })),
    },
    activeManifest:
      raw.activeManifest === null
        ? null
        : {
            ...raw.activeManifest,
            documentId: toDocumentId(raw.activeManifest.documentId),
            revisionId: toRevisionId(raw.activeManifest.revisionId),
          },
  };
}

export function toProjectHealth(raw: z.output<typeof projectHealthSchema>): ProjectHealth {
  return {
    ...raw,
    models: raw.models.map((model) => ({ ...model, modelId: toModelId(model.modelId) })),
  };
}
